import { useState } from 'react';
import axios from 'axios';
import { toast, TOAST_TYPE } from 'components/RevxMessage';
import i18n from 'i18n/config';
import { UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX } from 'utils/constants';

export interface InvoiceDownloadState {
  sendInvoiceIsDownloading: boolean;
  sendQuickBooksInvoiceIsDownloading: boolean;
  receiveInvoiceIsDownloading: boolean;
  receiveQuickBooksInvoiceIsDownloading: boolean;
}

export interface InvoiceDownloadHandlers {
  handleDownloadSendInvoice: (
    fileUrl: string,
    jobId: string | number
  ) => Promise<void>;
  handleDownloadSendQuickBooksInvoice: (
    fileUrl: string,
    jobId: string | number
  ) => Promise<void>;
  handleDownloadReceiveInvoice: (
    fileUrl: string,
    jobId: string | number
  ) => Promise<void>;
  handleDownloadReceiveQuickBooksInvoice: (
    fileUrl: string,
    jobId: string | number
  ) => Promise<void>;
}

export interface InvoiceDownloadUtils {
  getPDFLink: (fileUrl: string) => string;
  fallbackDownload: (url: string, fileName: string) => void;
  downloadFileFromServer: (fileUrl: string, fileName: string) => Promise<void>;
}

export interface UseInvoiceDownloadReturn
  extends InvoiceDownloadState,
    InvoiceDownloadHandlers,
    InvoiceDownloadUtils {}

export const useInvoiceDownload = (): UseInvoiceDownloadReturn => {
  const [sendInvoiceIsDownloading, setSendInvoiceIsDownloading] =
    useState(false);
  const [
    sendQuickBooksInvoiceIsDownloading,
    setSendQuickBooksInvoiceIsDownloading,
  ] = useState(false);
  const [receiveInvoiceIsDownloading, setReceiveInvoiceIsDownloading] =
    useState(false);
  const [
    receiveQuickBooksInvoiceIsDownloading,
    setReceiveQuickBooksInvoiceIsDownloading,
  ] = useState(false);

  const getPDFLink = (fileUrl: string): string => {
    return UPLOAD_FILE_THUMBNAIL_ADDRESS_PREFIX + fileUrl;
  };

  const fallbackDownload = (url: string, fileName: string): void => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadFileFromServer = async (
    fileUrl: string,
    fileName: string
  ): Promise<void> => {
    try {
      const authToken = localStorage.getItem('Authorization') ?? '';
      const url = `${process.env.REACT_APP_API_URL}/file/downloadFile`;
      console.log('Downloading from URL:', url);

      const res = await axios.post(
        url,
        {
          path: fileUrl,
          fileName: fileName,
        },
        {
          responseType: 'blob',
          headers: {
            'Access-Control-Allow-Credentials': true,
            'Access-Control-Allow-Origin': '*',
            Accept: '*/*',
            Authorization: authToken,
          },
        }
      );

      console.log('Response received:', res);

      if (!(res.data instanceof Blob)) {
        console.log('Invalid response format');
        return;
      }

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Server download failed:', error);
      toast(i18n.t('errors.serviceUnavailable'), TOAST_TYPE.error);
      throw error;
    }
  };

  const handleDownloadSendInvoice = async (
    fileUrl: string,
    jobId: string | number
  ): Promise<void> => {
    setSendInvoiceIsDownloading(true);
    const fileName = `REVSHOP_INVOICE_${jobId}.pdf`;
    try {
      await downloadFileFromServer(fileUrl, fileName);
    } catch (error) {
      console.error('Server download failed:', error);

      try {
        const url = getPDFLink(fileUrl);
        fallbackDownload(url, fileName);
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);

        const url = getPDFLink(fileUrl);
        window.open(url, '_blank');
      }
    } finally {
      setSendInvoiceIsDownloading(false);
    }
  };

  const handleDownloadSendQuickBooksInvoice = async (
    fileUrl: string,
    jobId: string | number
  ): Promise<void> => {
    setSendQuickBooksInvoiceIsDownloading(true);
    const fileName = `QUICKBOOKS_INVOICE_${jobId}.pdf`;

    try {
      await downloadFileFromServer(fileUrl, fileName);
    } catch (error) {
      console.error('Server download failed:', error);

      try {
        const url = getPDFLink(fileUrl);
        fallbackDownload(url, fileName);
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);

        const url = getPDFLink(fileUrl);
        window.open(url, '_blank');
      }
    } finally {
      setSendQuickBooksInvoiceIsDownloading(false);
    }
  };

  const handleDownloadReceiveInvoice = async (
    fileUrl: string,
    jobId: string | number
  ): Promise<void> => {
    setReceiveInvoiceIsDownloading(true);
    const fileName = `RECEIVE_JOB_INVOICE_${jobId}.pdf`;
    try {
      await downloadFileFromServer(fileUrl, fileName);
    } catch (error) {
      console.error('Server download failed:', error);

      try {
        const url = getPDFLink(fileUrl);
        fallbackDownload(url, fileName);
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);

        // Note: This should use receiveInvoiceFileUrl instead of sendInvoiceFileUrl
        const url = getPDFLink(fileUrl);
        window.open(url, '_blank');
      }
    } finally {
      setReceiveInvoiceIsDownloading(false);
    }
  };

  const handleDownloadReceiveQuickBooksInvoice = async (
    fileUrl: string,
    jobId: string | number
  ): Promise<void> => {
    setReceiveQuickBooksInvoiceIsDownloading(true);
    const fileName = `RECEIVE_QUICKBOOKS_INVOICE_${jobId}.pdf`;

    try {
      await downloadFileFromServer(fileUrl, fileName);
    } catch (error) {
      console.error('Server download failed:', error);

      try {
        const url = getPDFLink(fileUrl);
        fallbackDownload(url, fileName);
      } catch (fallbackError) {
        console.error('Fallback download failed:', fallbackError);

        const url = getPDFLink(fileUrl);
        window.open(url, '_blank');
      }
    } finally {
      setReceiveQuickBooksInvoiceIsDownloading(false);
    }
  };

  return {
    // State
    sendInvoiceIsDownloading,
    sendQuickBooksInvoiceIsDownloading,
    receiveInvoiceIsDownloading,
    receiveQuickBooksInvoiceIsDownloading,

    // Handlers
    handleDownloadSendInvoice,
    handleDownloadSendQuickBooksInvoice,
    handleDownloadReceiveInvoice,
    handleDownloadReceiveQuickBooksInvoice,

    // Utils
    getPDFLink,
    fallbackDownload,
    downloadFileFromServer,
  };
};
